import React, { useState } from 'react';
import VerificationCode from './VerificationCode';
import './EmailLogin.css';

interface EmailLoginProps {
  onLoginSuccess?: (user: any) => void;
  onLoginError?: (error: any) => void;
}

const EmailLogin: React.FC<EmailLoginProps> = ({
  onLoginSuccess,
  onLoginError
}) => {
  const [email, setEmail] = useState('');
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showVerification, setShowVerification] = useState(false);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (value) {
      setIsValidEmail(validateEmail(value));
    } else {
      setIsValidEmail(true);
    }
  };

  const handleEmailLogin = async () => {
    if (!email) {
      setIsValidEmail(false);
      return;
    }

    if (!validateEmail(email)) {
      setIsValidEmail(false);
      return;
    }

    setIsLoading(true);
    
    try {
      // Call backend API to send verification code
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/send-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        console.log('Verification code sent successfully:', email);
        setShowVerification(true);
      } else {
        onLoginError?.(data.message || 'Failed to send verification code');
      }
    } catch (error) {
      console.error('Failed to send verification code:', error);
      onLoginError?.('Network error, please try again later');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle verification code success
  const handleVerificationSuccess = (userData: any) => {
    console.log('Verification successful, userData:', userData);
    // 传递完整的用户数据给父组件
    onLoginSuccess?.(userData);
  };

  // Return to email input interface
  const handleBackToEmail = () => {
    setShowVerification(false);
  };

  // Resend verification code
  const handleResendCode = async () => {
    return handleEmailLogin();
  };

  // If showing verification code interface
  if (showVerification) {
    return (
      <VerificationCode
        email={email}
        onVerificationSuccess={handleVerificationSuccess}
        onBack={handleBackToEmail}
        onResendCode={handleResendCode}
      />
    );
  }

  // Email input interface
  return (
    <div className="email-login">
      <div className="email-input-section">
        <label className="email-label">Email</label>
        <div className={`email-input-container ${!isValidEmail ? 'error' : ''}`}>
          <input
            type="email"
            className="email-input"
            placeholder="Enter Email Address"
            value={email}
            onChange={handleEmailChange}
            disabled={isLoading}
          />
        </div>
        {!isValidEmail && (
          <span className="error-message">Please enter a valid email address</span>
        )}
      </div>

      <button 
        className={`email-login-button ${isLoading ? 'loading' : ''}`} 
        onClick={handleEmailLogin}
        disabled={isLoading}
      >
        <div className="email-button-content">
          {isLoading ? (
            <div className="loading-spinner"></div>
          ) : null}
          <span className="email-button-text">
            {isLoading ? 'Sending...' : 'Continue with Email'}
          </span>
        </div>
      </button>
    </div>
  );
};

export default EmailLogin;
